import React from 'react';
import { Link } from 'react-router-dom';

export default function MusicPolicy() {
  return (
    <div className="policy-page" data-testid="music-policy-page">
      <div className="policy-header">
        <h1>Music Policy</h1>
        <p className="policy-updated">Last updated: March 2025</p>
      </div>

      <div className="policy-content">
        <section className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            This Music Policy outlines the terms and guidelines governing all musical content
            released, distributed, and managed under the CloudfiMusic label, operated by
            Xuân Diệu Media Co., Ltd. By submitting, releasing, or collaborating on music
            through CloudfiMusic, artists and contributors agree to abide by this policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Content Standards</h2>
          <p>All music submitted to or released through CloudfiMusic must meet the following standards:</p>
          <ul>
            <li>
              <strong>Originality:</strong> All compositions, samples, and recordings must be
              original works or properly licensed. Artists are solely responsible for ensuring
              that their submissions do not infringe upon any third-party copyrights, trademarks,
              or intellectual property rights.
            </li>
            <li>
              <strong>Quality:</strong> Submissions must meet professional audio production standards.
              CloudfiMusic reserves the right to reject or request revisions to any submission that
              does not meet our quality benchmarks, including but not limited to mix clarity, mastering
              levels, and overall sonic cohesion.
            </li>
            <li>
              <strong>Genre Alignment:</strong> CloudfiMusic specializes in lofi, ambient, chillhop,
              and related genres. Submissions outside of our sonic identity may be declined at
              the label’s sole discretion.
            </li>
            <li>
              <strong>Prohibited Content:</strong> Music containing hate speech, explicit
              promotion of violence, unlicensed copyrighted material, or content that violates
              applicable laws will not be accepted under any circumstances.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>3. Intellectual Property & Ownership</h2>
          <ul>
            <li>
              Upon release through CloudfiMusic, the label obtains an <strong>exclusive, worldwide
              license</strong> to distribute, promote, and monetize the released tracks across all
              digital platforms, streaming services, and media channels for the duration of the
              agreement term.
            </li>
            <li>
              Artists retain <strong>songwriting and compositional credits</strong>. However,
              CloudfiMusic retains the right to the master recordings produced, mixed, or mastered
              under the label’s direction or funding.
            </li>
            <li>
              Any collaborative works produced under CloudfiMusic’s facilitation shall be
              co-owned by the participating artists and the label, with CloudfiMusic holding
              administrative rights for licensing and synchronization opportunities.
            </li>
            <li>
              Artists may not re-release, redistribute, or license tracks released under
              CloudfiMusic to competing labels or platforms without prior written consent from
              the label.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. Licensing & Synchronization</h2>
          <p>
            CloudfiMusic holds the exclusive right to negotiate and execute synchronization
            licenses (sync licenses) for all music in its catalog. This includes, but is not
            limited to, placements in:
          </p>
          <ul>
            <li>Film, television, and streaming media productions</li>
            <li>Advertisements and commercial campaigns</li>
            <li>Video games and interactive media</li>
            <li>Podcasts, YouTube content, and social media platforms</li>
            <li>Live events and public performances</li>
          </ul>
          <p>
            Revenue generated from synchronization placements will be shared between the
            artist and CloudfiMusic as outlined in the individual artist agreement. In the
            absence of a specific agreement, the default split shall be 60% to CloudfiMusic
            and 40% to the artist.
          </p>
        </section>

        <section className="policy-section">
          <h2>5. Sampling & Clearance</h2>
          <ul>
            <li>
              Artists using samples in their productions must provide proof of clearance or
              licensing for all sampled material prior to release.
            </li>
            <li>
              CloudfiMusic assumes no liability for copyright claims arising from uncleared
              samples. Artists shall indemnify and hold harmless CloudfiMusic against any
              such claims.
            </li>
            <li>
              Royalty-free and Creative Commons samples must be documented and disclosed
              at the time of submission.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>6. Playlist & Promotion Rights</h2>
          <p>
            By releasing through CloudfiMusic, artists grant the label the right to:
          </p>
          <ul>
            <li>Include their music in official CloudfiMusic playlists and compilations</li>
            <li>Use artist name, likeness, and biographical information for promotional purposes</li>
            <li>Submit tracks to editorial playlists, blogs, and media outlets on the artist’s behalf</li>
            <li>Create promotional content (visualizers, social media posts, teasers) using the released material</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>7. Removal & Takedown</h2>
          <ul>
            <li>
              CloudfiMusic reserves the right to remove any content from its catalog that
              violates this policy, is subject to a valid copyright claim, or damages the
              label’s reputation.
            </li>
            <li>
              Artists requesting voluntary removal of their music must provide <strong>90 days
              written notice</strong> to info@cloudfimusic.com. Early termination fees may apply
              as specified in the artist’s individual agreement.
            </li>
            <li>
              Music removed at the artist’s request may continue to appear in existing playlists
              and compilations for up to 30 days following the removal date.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>8. Amendments</h2>
          <p>
            CloudfiMusic reserves the right to update or modify this Music Policy at any time.
            Artists will be notified of material changes via email or through the official
            CloudfiMusic website. Continued release of music through the label following
            notification constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>9. Contact</h2>
          <p>
            For questions, disputes, or clarification regarding this Music Policy, please contact:
          </p>
          <p className="policy-contact">
            <strong>CloudfiMusic / Xuân Diệu Media Co., Ltd</strong><br />
            Representative: Xuan Chinh Nguyen<br />
            Email: <a href="mailto:info@cloudfimusic.com">info@cloudfimusic.com</a><br />
            Company ID: 0402222404
          </p>
        </section>

        <div className="policy-footer-nav">
          <Link to="/release-policy">Release Policy &rarr;</Link>
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
